import { useEffect, useRef, useState } from 'react';
import { motion } from '../lib/motion';
import { Award, Truck, Package2, Star } from 'lucide-react';
import type { HomepageContent } from '../lib/types';
import { REGISTRE, affichable, type CleFait } from '../data/registre';

/**
 * Les statistiques de la bande, tirées du registre de faits.
 *
 * Elles y étaient écrites en dur, dont une — « 75 ans d'expérience » — qui
 * mentait déjà de trois ans en 2026, tandis que la même page calculait
 * l'ancienneté correctement à deux endroits. Un chiffre figé se périme sans
 * prévenir.
 *
 * ⚠️ La liste est DÉCLARÉE ici, mais c'est le registre qui décide de ce qui
 * s'affiche. Un chiffre dont aucune source ne peut être nommée n'a rien à
 * afficher — voir `affichable()`. Deux des quatre entrées sont dans ce cas
 * aujourd'hui : « 40 déménagements par mois » et « 95% de satisfaction ». Elles
 * restent déclarées, et réapparaîtront d'elles-mêmes le jour où Gramme dira
 * d'où elles viennent : il n'y aura rien d'autre à faire que renseigner la
 * source dans le registre.
 *
 * La grille passe donc de quatre colonnes à deux, et c'est le comportement
 * voulu. Deux chiffres qu'on peut défendre valent mieux que quatre dont la
 * moitié s'effondre à la première question.
 */
const STATS_DECLAREES = [
  { icon: Award, cle: 'stats.anciennete', suffix: '+', label: "Ans d'expérience" },
  { icon: Truck, cle: 'stats.demenagements_par_mois', suffix: '+', label: 'Déménagements / mois' },
  { icon: Package2, cle: 'stats.capacite_m3', suffix: ' m³', label: 'Capacité par trajet' },
  { icon: Star, cle: 'stats.satisfaction', suffix: '%+', label: 'Satisfaction clients' },
] as const satisfies ReadonlyArray<{
  icon: React.ElementType;
  cle: CleFait;
  suffix: string;
  label: string;
}>;

const STATS = STATS_DECLAREES.filter((s) => affichable(s.cle));

/**
 * Compteur dont la valeur de départ est la valeur FINALE.
 *
 * B1 du plan correctif. La version précédente initialisait l'état à 0 et
 * comptait jusqu'à la cible depuis un `IntersectionObserver`. Aucun des deux
 * n'existe au pré-rendu : le HTML servi en production portait littéralement
 *
 *     <span class="…">0<!-- -->+</span>
 *
 * quatre fois, sur la même page qui annonce « +78 ans » ailleurs. Un moteur ou
 * un assistant qui lit le HTML y trouvait quatre zéros.
 *
 * L'ordre est donc inversé : la valeur finale est rendue d'emblée — serveur et
 * première hydratation d'accord, aucun écart —, et l'animation ne se déclenche
 * que si le composant est HORS de la fenêtre au montage. C'est la seule
 * situation où repartir de zéro se voit comme une animation plutôt que comme un
 * clignotement : quand la bande est déjà à l'écran, il n'y a rien à animer, et
 * on ne l'anime pas.
 *
 * Une personne qui a demandé moins de mouvement n'en reçoit aucun.
 */
function useCompteur(cible: number, ref: React.RefObject<HTMLElement>) {
  const [valeur, setValeur] = useState(cible);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const moinsDeMouvement =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (moinsDeMouvement) return;

    const rect = element.getBoundingClientRect();
    const dejaVisible = rect.top < window.innerHeight && rect.bottom > 0;
    if (dejaVisible) return;

    // Hors champ : on peut repartir de zéro sans que personne voie le saut.
    setValeur(0);

    let interval: ReturnType<typeof setInterval> | undefined;
    const observateur = new IntersectionObserver(
      ([entree]) => {
        if (!entree.isIntersecting) return;
        observateur.disconnect();
        const pas = 24;
        let courant = 0;
        interval = setInterval(() => {
          courant += cible / pas;
          if (courant >= cible) {
            setValeur(cible);
            clearInterval(interval);
          } else {
            setValeur(Math.floor(courant));
          }
        }, 800 / pas);
      },
      { threshold: 0.3 },
    );
    observateur.observe(element);

    return () => {
      observateur.disconnect();
      if (interval) clearInterval(interval);
    };
  }, [cible, ref]);

  return valeur;
}

function CarteStat({
  icon: Icon,
  valeur,
  suffix,
  label,
  derniere,
}: {
  icon: React.ElementType;
  valeur: number;
  suffix: string;
  label: string;
  derniere: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const affichee = useCompteur(valeur, ref);

  return (
    <div ref={ref} className="relative flex flex-col items-center py-10 px-6">
      <div className="w-14 h-14 rounded-full bg-yellow/15 flex items-center justify-center mb-4">
        <Icon className="w-7 h-7 text-yellow" />
      </div>
      <span className="font-black text-5xl md:text-6xl leading-none text-yellow tabular-nums">
        {affichee}{suffix}
      </span>
      <span className="text-white/60 text-xs uppercase tracking-[0.15em] mt-2 text-center">
        {label}
      </span>
      {!derniere && (
        <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 w-px h-16 bg-yellow/20" />
      )}
    </div>
  );
}

interface Props {
  data?: HomepageContent['stats'] | null;
}

export default function StatsStrip({ data }: Props) {
  // Prop conservée pour la compatibilité du CMS — les valeurs viennent du registre.
  void data;

  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6 }}
      className="bg-[#0C2094] py-4 md:py-0"
    >
      {/* Le nombre de colonnes suit le nombre de statistiques RÉELLEMENT
          affichables : une grille figée à quatre colonnes laisserait deux
          cases vides le jour où un chiffre perd sa source. */}
      <div
        className={`max-w-6xl mx-auto px-4 grid grid-cols-2 ${
          STATS.length >= 4 ? 'md:grid-cols-4' : STATS.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'
        }`}
      >
        {STATS.map((stat, i) => (
          <CarteStat
            key={stat.label}
            icon={stat.icon}
            valeur={REGISTRE[stat.cle].valeur as number}
            suffix={stat.suffix}
            label={stat.label}
            derniere={i === STATS.length - 1}
          />
        ))}
      </div>
    </motion.section>
  );
}
